import { useContext, useEffect, useRef, useState } from 'react';

import styles from './WaitingForJoiningScreen.module.css';

import { GlobalContext } from "../../contexts/globalContext";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { byPrefixAndName } from '@awesome.me/kit-fe6b9b7683/icons'

import microfon from '../../graphics/conference/ic-microfon-moderator.svg';
import waitingAnim from '../../graphics/conference/wait-start-anim.json';
import IODevicesComponent from './IODevicesComponent';

import Lottie from 'react-lottie-player';


const WaitingForJoiningScreen = ({ SetWaitingForJoining }) => {
    const {
        conferencePaused, currentParticipant, currentConference, socketRef, setCurrentConference, setCurrentTopic, invitationData, deskData, setIoSettings, setAlertMessage
    } = useContext(GlobalContext);


    const inputRefs = useRef([]);
    const videoRef = useRef();
    const audioRef = useRef(null);
    const nameRef = useRef(null);

    const [showStartConferenceForm, setShowStartConferenceForm] = useState(false);
    const [showJoinButton, setShowJoinButton] = useState(false);
    const [localVideoStream, setLocalVideoStream] = useState(null);
    const [localAudioStream, setLocalAudioStream] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [digitValues, setDigitValues] = useState(['', '', '', '']);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState();
    const [selectedMicDevice, setSelectedMicDevice] = useState();
    const [micMuted, setMicMuted] = useState(false);
    const [videoMuted, setVideoMuted] = useState(false);
    const [videoDevices, setVideoDevices] = useState([])
    const [micDevices, setMicDevices] = useState([])
    const [isScrollable, setIsScrollable] = useState(false);
    const [devicesListOpen, setDevicesListOpen] = useState(false)

    useEffect(() => {
        if (deskData) return;

        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const videos = devices.filter(d => d.kind === 'videoinput' && !d.label.includes("OBS"));
                const mics = devices.filter(d => d.kind === 'audioinput');

                setVideoDevices(videos);
                setMicDevices(mics);


                if (videos[0]) {
                    setSelectedVideoDevice(videos[0]);
                } else {
                    console.error("No video devices.")
                }

                if (mics[0]) {
                    setSelectedMicDevice(mics[0]);
                } else {
                    console.error("No microphone devices.")
                }
            })
            .catch(err => console.error("Error listing media devices:", err));

        if (!currentConference) {
            SetWaitingForJoining(false)
        }
    }, []);

    useEffect(() => {
        if (deskData) return;

        if (!selectedVideoDevice) return;

        if (selectedVideoDevice === "stop") {
            videoRef.current.pause()
            return;
        }

        const constraints = {
            video: selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice.deviceId } } : false,
            audio: false,
        };


        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
                if (localVideoStream) {
                    localVideoStream.getTracks().forEach(track => track.stop());
                }

                setLocalVideoStream(stream);

                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }

            })
            .catch(err => console.error("Error accessing media devices:", err));
    }, [selectedVideoDevice]);

    useEffect(() => {
        if (deskData) return;

        if (!selectedMicDevice) return;

        const constraints = {
            video: false,
            audio: selectedMicDevice ? { deviceId: { exact: selectedMicDevice.deviceId } } : false
        };

        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
                if (localAudioStream) {
                    localAudioStream.getTracks().forEach(track => track.stop());
                }

                setLocalAudioStream(stream);

                if (audioRef.current) {
                    audioRef.current.srcObject = stream;
                    audioRef.current.play();
                }
            })
            .catch(err => console.error("Error accessing media devices:", err));

    }, [selectedMicDevice]);

    useEffect(() => {
        if (!deskData && localAudioStream) {
            if (micMuted) {
                localAudioStream.getAudioTracks()[0].enabled = false;
            } else {
                localAudioStream.getAudioTracks()[0].enabled = true;
            }
        }
    }, [micMuted]);

    useEffect(() => {
        if (!deskData && localVideoStream) {
            if (videoMuted) {
                localVideoStream.getVideoTracks()[0].enabled = false;
            } else {
                localVideoStream.getVideoTracks()[0].enabled = true;
            }
        }
    }, [videoMuted]);

    useEffect(() => {
        if (currentParticipant) {
            socketRef.current.on('CONFERENCE_STARTED', async (conference) => {
                if (currentConference._id === conference._id) {
                    if (conference.type === "Conference") {
                        setCurrentTopic(conference.topics[0])
                    }
                    setCurrentConference(conference);

                    setShowJoinButton(true);
                }
            });

            if (!invitationData) {
                if (currentConference.status === "New" && currentConference.type !== "Presentation") {
                    if (currentConference.topics[0] && currentConference.topics[0].moderator?._id === currentParticipant?._id) {
                        setShowStartConferenceForm(true);
                    }
                } else {
                    setShowJoinButton(true);
                }
            } else {
                if (currentConference.status === "Active") {
                    setShowJoinButton(true);
                }
            }

            return () => {
                socketRef.current?.off('CONFERENCE_STARTED');
            };
        }
    }, []);

    useEffect(() => {
        if (activeIndex < digitValues.length) {
            inputRefs.current[activeIndex]?.focus();
        }
    }, [activeIndex, digitValues]);

    useEffect(() => {
        if (digitValues.every(digit => digit === '')) {
            setActiveIndex(0);
            inputRefs.current[0]?.focus();
        } else if (activeIndex >= 0 && activeIndex < digitValues.length) {
            inputRefs.current[activeIndex]?.focus();
        }
    }, [activeIndex, digitValues]);

    const UnpauseConference = () => {
        socketRef.current.emit("PLAY_CONFERENCE", currentConference._id);
    }

    const StartConference = () => {
        if (digitValues.some(digit => digit === '')) {
            setAlertMessage({ type: "ERROR", message: "Please enter a valid 4-digit code." });
            return;
        }

        const code = digitValues.join('');
        socketRef.current.emit("START_CONFERENCE", currentConference._id, code, null, (data) => {
            if (data.error) {
                console.log(data.response);
            } else {
                setShowStartConferenceForm(false);
                SetWaitingForJoining(false);
            }
        });
    };

    const StopConference = () => {
        if (digitValues.some(digit => digit === '')) {
            setAlertMessage({ type: "ERROR", message: "Please enter the 4-digit code in order to stop the conference." });
            return;
        }

        const code = digitValues.join('');
        socketRef.current.emit("STOP_CONFERENCE", currentConference._id, code);
    };

    const handleFocus = (index) => {
        setActiveIndex(index);
        inputRefs.current[index]?.focus();
    };

    const handleBlur = () => {
        setActiveIndex(-1);
    };

    const handleKeyDown = (index, event) => {
        const updatedDigits = [...digitValues];

        if (event.key === 'Backspace') {
            if (updatedDigits[index] !== '') {
                updatedDigits[index] = '';
                setDigitValues(updatedDigits);
                setActiveIndex(index);
            } else if (index > 0) {
                setActiveIndex(index - 1);
                inputRefs.current[index - 1]?.focus();
                updatedDigits[index - 1] = '';
                setDigitValues(updatedDigits);
            }
        }
    };

    const handleChange = (index, event) => {
        const { value } = event.target;
        const updatedDigits = [...digitValues];

        if (value.length <= 1) {
            updatedDigits[index] = value;
            setDigitValues(updatedDigits);

            if (value !== '' && index < digitValues.length - 1) {
                setActiveIndex(index + 1);
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyboardClick = (value) => {
        const updatedDigits = [...digitValues];

        if (value === 'x') {
            const allEmpty = updatedDigits.every(digit => digit === '');
            if (allEmpty) return; // Do nothing if all digits are empty

            const lastFilledIndex = updatedDigits.findIndex(digit => digit === '');
            const deleteIndex = lastFilledIndex === -1 ? updatedDigits.length - 1 : lastFilledIndex - 1;

            if (updatedDigits[deleteIndex] !== '') {
                updatedDigits[deleteIndex] = '';
                setDigitValues(updatedDigits);
                setActiveIndex(deleteIndex);
            } else if (deleteIndex > 0) {
                setActiveIndex(deleteIndex - 1);
                inputRefs.current[deleteIndex - 1]?.focus();
                updatedDigits[deleteIndex - 1] = '';
                setDigitValues(updatedDigits);
            }
        } else {
            const emptyIndex = updatedDigits.findIndex(digit => digit === '');
            if (emptyIndex !== -1) {
                updatedDigits[emptyIndex] = value;
                setDigitValues(updatedDigits);

                const nextIndex = emptyIndex + 1 < updatedDigits.length ? emptyIndex + 1 : emptyIndex;
                setActiveIndex(nextIndex);
                inputRefs.current[nextIndex]?.focus();
            }
        }
    };

    const JoinConference = () => {

        if (!deskData) {
            var settings = {
                audio: selectedMicDevice ? { deviceId: { exact: selectedMicDevice.deviceId } } : false,
                video: selectedVideoDevice ? {
                    deviceId: { exact: selectedVideoDevice.deviceId },
                    video: {
                        facingMode: "user",
                        width: {
                            min: 640,
                            max: 960,
                        },
                        height: {
                            min: 400,
                            max: 540,
                        }
                    }
                } : false,
                micMuted,
                videoMuted
            }
            setIoSettings(settings);
        }
        SetWaitingForJoining(false);
    }

    useEffect(() => {
        const checkScroll = () => {
            if (nameRef.current) {
                const containerWidth = nameRef.current.offsetWidth;
                const textWidth = nameRef.current.scrollWidth;
                setIsScrollable(textWidth > containerWidth);
            }
        };

        setTimeout(checkScroll, 0);

        window.addEventListener("resize", checkScroll);
        return () => window.removeEventListener("resize", checkScroll);
    }, [currentParticipant?.name]);

    return (
        <div style={{ display: 'flex', flexDirection: 'row', height: '100%', width: '100%', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <div className={styles.component} style={{ display: "flex", flexDirection: "column", alignItems: "center", flexWrap: "" }}>
                <div style={{ marginTop: 45, display: 'flex', gap: '20px', width: '1100px', height: '718px', flexWrap: 'wrap', justifyContent: 'center', position: "relative" }}>
                    <div style={{ height: 700, overflow: "hidden", borderRadius: 20 }}>
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            poster={process.env.REACT_APP_CMS_URL + "/assets/flags/" + currentParticipant?.country + ".svg"}
                            style={{ width: '100%', height: '1100px', marginTop: -200 }}
                        />
                        {!deskData && <div className={styles.ioDevicesComp}>
                            <div className={videoMuted ? styles.ioDeviceButtonMuted : styles.ioDeviceButton} onClick={() => setVideoMuted(!videoMuted)}>
                                <FontAwesomeIcon icon={byPrefixAndName.fal[videoMuted ? 'camera-web-slash' : 'camera-web']} />

                            </div>
                            <div className={micMuted ? styles.ioDeviceButtonMuted : styles.ioDeviceButton} onClick={() => setMicMuted(!micMuted)}>
                                <FontAwesomeIcon icon={byPrefixAndName.fal[micMuted ? 'microphone-slash' : 'microphone']} />
                            </div>
                            <div className={devicesListOpen ? styles.ioDeviceButtonOpened : styles.ioDeviceButton} onClick={(e) => {
                                e.preventDefault()
                                setDevicesListOpen(!devicesListOpen)

                            }}>
                                <FontAwesomeIcon icon={byPrefixAndName.fal['sliders']} />
                            </div>
                        </div>}
                        {devicesListOpen && <IODevicesComponent selectedVideoDevice={selectedVideoDevice} setSelectedVideoDevice={setSelectedVideoDevice} selectedMicDevice={selectedMicDevice}
                            setSelectedMicDevice={setSelectedMicDevice} videoDevices={videoDevices} micDevices={micDevices} setDevicesListOpen={setDevicesListOpen} />}
                    </div>
                    {showStartConferenceForm && <img src={microfon} className={styles.customElement} />}

                </div>

                <audio ref={audioRef} style={{ display: 'none' }} />

                <div style={{ flex: 1, height: 100, width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>

                    <label className={styles.welcomeText} >Welcome!</label>

                    {/* <label className={styles.nameText}>{currentParticipant?.name}</label> */}

                    <label className={`${styles.nameText} ${isScrollable ? styles.scrollActive : ""}`} ref={nameRef}>
                        <span>{currentParticipant?.name}</span>
                    </label>



                    {showStartConferenceForm && (
                        <div className={styles.bottomButtons} style={{ bottom: 89 }}>
                            <div className={styles.confModeratorTextDiv}>
                                <label className={styles.confModeratorText}>CONFERENCE MODERATOR</label>
                            </div>
                            <label className={styles.codeText}>Please enter the 4-digit code:</label>
                            <div className={styles.digitsDiv}>
                                {digitValues.map((digit, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.inputDigitFocused} 
                                            ${index === 0 ? styles.first : ''} 
                                            ${activeIndex === index ? styles.active : ''} 
                                            ${digit !== '' ? styles.completedValue : ''}`}
                                        onClick={() => handleFocus(index)}
                                    >
                                        <input
                                            ref={(el) => (inputRefs.current[index] = el)}
                                            className={styles.inputDigit}
                                            type="text"
                                            maxLength="1"
                                            value={digit}
                                            onFocus={() => handleFocus(index)}
                                            onBlur={handleBlur}
                                            onChange={(e) => handleChange(index, e)}
                                            onKeyDown={(e) => handleKeyDown(index, e)}
                                        />
                                        {digit !== '' && (
                                            <span className={styles.completedLabel}>*</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className={styles.startButton} onClick={StartConference}>
                                Start the {currentConference.type}
                            </div>

                            <div className={styles.stopButton} onClick={StopConference}>
                                Stop the {currentConference.type}
                            </div>
                        </div>
                    )}

                    {(!showStartConferenceForm && !showJoinButton) && (
                        <div className={styles.bottomButtons} style={{ bottom: 146 }}>
                            <Lottie
                                loop={true}
                                play={true}
                                animationData={waitingAnim}
                                style={{ width: 320, height: 160, marginTop: 98 }}
                            />

                            <div className={styles.confModeratorTextDiv} style={{
                                marginTop: 56,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                alignContent: 'center'
                            }}>
                                <label className={styles.confModeratorText}>STARTING SOON</label>
                            </div>

                            <label className={styles.customText} style={{ marginTop: 50 }}> Thank you for your patience </label>

                            <label className={styles.customTextSmall}>The moderator will start the {currentConference.type} shortly</label>

                        </div>
                    )}

                    {showStartConferenceForm && (
                        <div className={styles.digitsKeyboardElement}>
                            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'x'].map((key, index) => (
                                <div
                                    key={index}
                                    className={styles.digitkeyboard}
                                    onClick={() => handleKeyboardClick(key)}
                                    style={{ opacity: key === '' ? 0 : 1 }}
                                >
                                    <label className={styles.digitTextKeyboard}>{key === 'x' ?
                                        <i className="fa-thin fa-delete-left" style={{
                                            bottom: '50%',
                                            transform: 'translate(-25%, 50%)', position: "relative"
                                        }}></i> : key}</label>
                                </div>
                            ))}
                        </div>
                    )}

                    {showJoinButton && (
                        <div className={styles.bottomButtons}>
                            <div className={styles.confModeratorTextDiv} style={{
                                // width: 194,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                alignContent: 'center',
                                marginTop: 100
                            }}>
                                {!conferencePaused && <label className={styles.confModeratorText}>IN PROGRESS</label>}
                                {conferencePaused && <label className={styles.confModeratorText}>PAUSED</label>}
                            </div>
                            <Lottie
                                loop={true}
                                play={true}
                                animationData={waitingAnim}
                                style={{ width: 320, height: 160, marginBottom: 50 }}
                            />
                            {!conferencePaused && <div className={styles.customText}> Press the button to enter </div>}
                            {currentConference?.moderatedBy === currentParticipant?._id && conferencePaused && <div className={styles.customText} style={{ fontSize: "64px" }}>
                                Press to resume the {currentConference.type}.
                            </div>}
                            {currentConference?.moderatedBy !== currentParticipant?._id && conferencePaused && <div className={styles.customText} style={{ fontSize: "64px" }}>
                                Please wait.
                            </div>}
                            <div>
                                {!conferencePaused && <button type="text" onClick={() => JoinConference()} className={styles.joinButton}>
                                    Join {currentConference.type}</button>
                                }
                                {(currentConference?.moderatedBy === currentParticipant?._id || (currentConference?.type === "Conference" &&
                                    currentConference?.topics[0]?.moderator._id === currentParticipant?._id)) && conferencePaused &&
                                    <button type="text" onClick={() => UnpauseConference()} className={styles.joinButton}>
                                        Resume {currentConference.type}
                                    </button>
                                }
                            </div>
                        </div>
                    )}
                </div>


            </div>

        </div>
    );
}

export default WaitingForJoiningScreen;
